const contentLink = (n) => {
    const p = n.payload || {};
    const commentQuery = p.commentId && n.targetType !== 'Post' ? `?comment=${p.commentId}` : '';

    if (n.targetType === 'UpdateComment') {
        return p.updateSlug ? `/info/updates/${p.updateSlug}${commentQuery}` : null;
    }

    const slug = p.postSlug ?? p.postId;
    if (!slug) return null;

    const base = p.categorySlug ? `/${p.categorySlug}/${slug}` : `/user/${p.postAuthorId}/${slug}`;
    return `${base}${commentQuery}`;
};

export const getNotificationLink = (n) => {
    switch (n.type) {
        case 'Reply':
        case 'PostComment':
        case 'Mention':
        case 'Like':
            return contentLink(n);
        case 'NewFollower':
            return n.actor ? `/user/${n.actor.id}` : null;
        case 'Achievement':
            return '/profile';
        case 'SanctionIssued':
        case 'SanctionRevoked':
            return '/info/rules';
        default:
            return null;
    }
};

const MODERATION_KEYS = {
    ContentHidden: 'hidden',
    ContentDeleted: 'deleted',
    ContentRestored: 'restored',
};

const sanctionContent = (n, t, language) => {
    const p = n.payload || {};
    const restrictions = (p.restriction || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(r => t(`errors.restricted.types.${r}`, { defaultValue: r }))
        .join(', ');

    const parts = [];
    if (p.reason) parts.push(`${t('notifications.reason')}: ${p.reason}`);
    if (n.type === 'SanctionIssued') {
        parts.push(p.expiresAt
            ? t('errors.restricted.until', { date: new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(p.expiresAt)) })
            : t('errors.restricted.permanent'));
    }

    const key = n.type === 'SanctionIssued' ? 'sanction_issued' : 'sanction_revoked';
    return { text: t(`notifications.${key}`, { restrictions }), secondary: parts.join('. ') || null };
};

export const getNotificationContent = (n, t, language) => {
    const p = n.payload || {};
    const name = n.actor?.displayName || n.actor?.username || t('notifications.someone');
    const others = Math.max(0, (n.count || 1) - 1);
    const isPost = n.targetType === 'Post';

    switch (n.type) {
        case 'Like': {
            const base = isPost ? 'like_post' : 'like_comment';
            return {
                text: others > 0
                    ? t(`notifications.${base}_others`, { name, count: others })
                    : t(`notifications.${base}`, { name }),
                secondary: isPost ? p.postTitle : p.snippet,
            };
        }
        case 'Reply':
            return { text: t('notifications.reply', { name }), secondary: p.snippet };
        case 'PostComment':
            return {
                text: n.count > 1
                    ? t('notifications.post_comments_total', { count: n.count })
                    : t('notifications.post_comment', { name }),
                secondary: p.postTitle,
            };
        case 'Mention':
            return { text: t('notifications.mention', { name }), secondary: p.snippet };
        case 'NewFollower':
            return { text: t('notifications.new_follower', { name }), secondary: null };
        case 'ContentHidden':
        case 'ContentDeleted':
        case 'ContentRestored':
            return {
                text: t(`notifications.content_${MODERATION_KEYS[n.type]}_${isPost ? 'post' : 'comment'}`),
                secondary: p.snippet,
            };
        case 'SanctionIssued':
        case 'SanctionRevoked':
            return sanctionContent(n, t, language);
        case 'ReportResolved':
            return {
                text: t(p.outcome === 'Dismissed' ? 'notifications.report_dismissed' : 'notifications.report_resolved'),
                secondary: p.snippet,
            };
        case 'Achievement':
            return { text: t('notifications.achievement', { name: p.achievementName }), secondary: null };
        case 'ComplaintResolved':
            return { text: t('notifications.complaint_resolved'), secondary: p.snippet };
        case 'System':
            return { text: p.title || t('notifications.system'), secondary: p.text };
        default:
            return { text: t('notifications.unknown'), secondary: null };
    }
};
